import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Alert,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../../stores/authStore";
import { useRBAC } from "../../hooks/useRBAC";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import SortableTableCell from "../../components/layout/SortableTableCell";
import TableActionButton from "../../components/layout/TableActionButton";
import { useTableSort } from "../../hooks/useTableSort";
import { branchAPI } from "../../services/branchAPI";
import { categoryAPI } from "../../services/categoryAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import {
  WORK_CATEGORY_OPTIONS,
  workCategoryLabel,
  normalizeWorkCategory,
} from "../../constants/workCategories";
import {
  productSalesPointAPI,
  ProductSalesPointItem,
  ProductPriceScheme,
  ProductSalesPointUpdateData,
  ProductMasterSearchItem,
} from "../../services/productSalesPointAPI";

type ProductListSortField = "name" | "sku" | "category" | "sellingPrice";

const EDIT_UNITS = ["PCS", "KG", "GM", "LTR", "ML", "BOX", "PKT", "PACK", "DOZEN"];

const money = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) ? `₹${n.toFixed(2)}` : "—";
};

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25 }}>
      {value == null || value === "" ? "—" : value}
    </Typography>
  </Grid>
);

type EditProductForm = {
  name: string;
  sku: string;
  barcode: string;
  itemCode: string;
  hsnCode: string;
  unit: string;
  categoryId: string;
  status: "ACTIVE" | "INACTIVE";
  availability: "DINE_IN" | "TAKEAWAY" | "DELIVERY" | "ALL";
  hideFromBilling: boolean;
  allowFractionalQty: boolean;
  basePrice: string;
  purchasePrice: string;
  sellingPrice: string;
  wholesalePrice: string;
  mrp: string;
  discountType: "" | "PERCENT" | "FLAT";
  discountValue: string;
  gstPercentage: string;
  isTaxInclusive: boolean;
  pricingMode: "QUANTITY_BASED" | "AMOUNT_BASED";
  pricePerUnit: string;
};

const emptyEditForm = (): EditProductForm => ({
  name: "",
  sku: "",
  barcode: "",
  itemCode: "",
  hsnCode: "",
  unit: "PCS",
  categoryId: "",
  status: "ACTIVE",
  availability: "ALL",
  hideFromBilling: false,
  allowFractionalQty: false,
  basePrice: "0",
  purchasePrice: "0",
  sellingPrice: "0",
  wholesalePrice: "",
  mrp: "",
  discountType: "",
  discountValue: "",
  gstPercentage: "0",
  isTaxInclusive: false,
  pricingMode: "QUANTITY_BASED",
  pricePerUnit: "",
});

type SchemeForm = {
  name: string;
  schemePrice: string;
  startsAt: string;
  endsAt: string;
};

const emptySchemeForm = (): SchemeForm => ({
  name: "",
  schemePrice: "",
  startsAt: new Date().toISOString().slice(0, 10),
  endsAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10),
});

const rowToEditForm = (row: ProductSalesPointItem): EditProductForm => ({
  name: row.name || "",
  sku: row.sku || "",
  barcode: row.barcode || "",
  itemCode: row.itemCode || "",
  hsnCode: row.hsnCode || "",
  unit: row.unit || "PCS",
  categoryId: row.categoryId || row.category?.id || "",
  status: row.status === "INACTIVE" ? "INACTIVE" : "ACTIVE",
  availability:
    row.availability === "DINE_IN" ||
    row.availability === "TAKEAWAY" ||
    row.availability === "DELIVERY" ||
    row.availability === "ALL"
      ? row.availability
      : "ALL",
  hideFromBilling: Boolean(row.hideFromBilling),
  allowFractionalQty: Boolean(row.allowFractionalQty),
  basePrice: String(row.basePrice ?? 0),
  purchasePrice: String(row.purchasePrice ?? 0),
  sellingPrice: String(row.sellingPrice ?? 0),
  wholesalePrice:
    row.wholesalePrice != null ? String(row.wholesalePrice) : "",
  mrp: row.mrp != null ? String(row.mrp) : "",
  discountType: row.discountType ?? "",
  discountValue: row.discountValue != null ? String(row.discountValue) : "",
  gstPercentage: String(row.gstPercentage ?? 0),
  isTaxInclusive: Boolean(row.isTaxInclusive),
  pricingMode: row.pricingMode === "AMOUNT_BASED" ? "AMOUNT_BASED" : "QUANTITY_BASED",
  pricePerUnit: row.pricePerUnit != null ? String(row.pricePerUnit) : "",
});

const ProductListPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { canCreate, canUpdate, canDelete, hasAnyRole, canChangePosScope } =
    useRBAC();
  const canChangeScope = canChangePosScope();
  const isOperator = hasAnyRole("OPERATOR");
  const pageTitle = isOperator ? "Sales Point Products" : "Product List";
  const importLabel = isOperator ? "Add from catalog" : "Import";

  const [selectedBranch, setSelectedBranch] = useState(user?.branchId ?? "");
  const [selectedSalesPoint, setSelectedSalesPoint] = useState(
    user?.salesPointId ?? ""
  );
  const salesPointId = canChangeScope
    ? selectedSalesPoint
    : user?.salesPointId ?? "";

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "",
    sellingPrice: "",
    unit: "PCS",
    barcode: "",
    pricingMode: "QUANTITY_BASED" as "QUANTITY_BASED" | "AMOUNT_BASED",
    allowFractionalQty: false,
  });

  const { sortBy, sortOrder, handleSort, sortParams } =
    useTableSort<ProductListSortField>("name", "asc");
  const onSort = (field: ProductListSortField) => handleSort(field, () => setPage(0));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importSearch, setImportSearch] = useState("");
  const [debouncedImportSearch, setDebouncedImportSearch] = useState("");
  const [importWorkCategory, setImportWorkCategory] = useState<string>("all");
  const [masterProducts, setMasterProducts] = useState<ProductMasterSearchItem[]>([]);
  const [selectedMasterIds, setSelectedMasterIds] = useState<string[]>([]);
  const [loadingMaster, setLoadingMaster] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editRow, setEditRow] = useState<ProductSalesPointItem | null>(null);
  const [editForm, setEditForm] = useState<EditProductForm>(emptyEditForm);
  const [viewRow, setViewRow] = useState<ProductSalesPointItem | null>(null);
  const [schemes, setSchemes] = useState<ProductPriceScheme[]>([]);
  const [schemeForm, setSchemeForm] = useState<SchemeForm>(emptySchemeForm);
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [savingScheme, setSavingScheme] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { data: branches = [], isLoading: branchesLoading } = useQuery({
    queryKey: ["branches", "all"],
    queryFn: () => branchAPI.getAllBranches(),
    enabled: canChangeScope,
  });

  const { data: salesPoints = [], isLoading: salesPointsLoading } = useQuery({
    queryKey: ["sales_points", selectedBranch],
    queryFn: () =>
      selectedBranch
        ? salesPointAPI.getSalesPointsByBranch(selectedBranch)
        : Promise.resolve([]),
    enabled: canChangeScope && !!selectedBranch,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["categories", "product-list-edit"],
    queryFn: () => categoryAPI.getCategories({ pageSize: 200, is_active: true }),
    enabled: !!editRow || createOpen || !!viewRow,
  });
  const categories = categoriesData?.data ?? [];

  useEffect(() => {
    if (!canChangeScope) return;
    if (user?.branchId && !selectedBranch) {
      setSelectedBranch(user.branchId);
    }
  }, [canChangeScope, user?.branchId, selectedBranch]);

  useEffect(() => {
    if (!canChangeScope || !selectedBranch || salesPointsLoading) return;
    if (
      selectedSalesPoint &&
      salesPoints.some((sp) => sp.id === selectedSalesPoint)
    ) {
      return;
    }
    if (user?.salesPointId && salesPoints.some((sp) => sp.id === user.salesPointId)) {
      setSelectedSalesPoint(user.salesPointId);
      return;
    }
    if (selectedSalesPoint) {
      setSelectedSalesPoint("");
    }
  }, [
    canChangeScope,
    selectedBranch,
    salesPoints,
    salesPointsLoading,
    selectedSalesPoint,
    user?.salesPointId,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "productSalesPointList",
      salesPointId,
      page,
      rowsPerPage,
      debouncedSearch,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      productSalesPointAPI.listBySalesPoint(salesPointId, {
        pageNo: page + 1,
        pageSize: rowsPerPage,
        ...sortParams,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      }),
    enabled: !!salesPointId,
  });

  const items = data?.data ?? [];
  const totalCount = data?.total ?? 0;

  const scopeFilters = canChangeScope ? (
    <>
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel id="product-list-branch-label">Branch</InputLabel>
        <Select
          labelId="product-list-branch-label"
          label="Branch"
          value={selectedBranch}
          displayEmpty
          disabled={branchesLoading}
          onChange={(e) => {
            setSelectedBranch(String(e.target.value));
            setSelectedSalesPoint("");
            setPage(0);
            setSearch("");
          }}
        >
          <MenuItem value="">
            <em>Select branch</em>
          </MenuItem>
          {branches.map((b) => (
            <MenuItem key={b.id} value={b.id}>
              {b.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel id="product-list-sp-label">Sales point</InputLabel>
        <Select
          labelId="product-list-sp-label"
          label="Sales point"
          value={selectedSalesPoint}
          displayEmpty
          disabled={!selectedBranch || salesPointsLoading}
          onChange={(e) => {
            setSelectedSalesPoint(String(e.target.value));
            setPage(0);
            setSearch("");
          }}
        >
          <MenuItem value="">
            <em>Select sales point</em>
          </MenuItem>
          {salesPoints.map((sp) => (
            <MenuItem key={sp.id} value={sp.id}>
              {sp.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  ) : null;

  const closeCreateDialog = () => {
    setCreateOpen(false);
    setCreateForm({
      name: "",
      sellingPrice: "",
      unit: "PCS",
      barcode: "",
      pricingMode: "QUANTITY_BASED",
      allowFractionalQty: false,
    });
  };

  const handleCreateDirect = async () => {
    if (!salesPointId) return;
    const name = createForm.name.trim();
    const price = Number(createForm.sellingPrice);
    if (!name) {
      setAlert({ type: "error", message: "Name is required" });
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      setAlert({ type: "error", message: "Valid selling price is required" });
      return;
    }
    setCreating(true);
    try {
      await productSalesPointAPI.createDirect({
        salesPointId,
        name,
        sellingPrice: price,
        unit: createForm.unit || "PCS",
        barcode: createForm.barcode.trim() || null,
        pricingMode: createForm.pricingMode,
        allowFractionalQty: createForm.allowFractionalQty,
      });
      closeCreateDialog();
      setAlert({ type: "success", message: "Product added to sales point" });
      queryClient.invalidateQueries({ queryKey: ["productSalesPointList"] });
    } catch (e) {
      setAlert({
        type: "error",
        message: e instanceof Error ? e.message : "Failed to add product",
      });
    } finally {
      setCreating(false);
    }
  };

  const closeImportDialog = () => {
    setImportOpen(false);
    setImportSearch("");
    setDebouncedImportSearch("");
    setImportWorkCategory("all");
    setMasterProducts([]);
    setSelectedMasterIds([]);
  };

  useEffect(() => {
    if (!importOpen || !salesPointId) return;
    let cancelled = false;
    salesPointAPI
      .getSalesPointById(salesPointId)
      .then((sp) => {
        if (cancelled) return;
        const wt = String(sp?.workstationType || "")
          .trim()
          .toLowerCase()
          .replace(/[\s-]+/g, "_");
        if (wt) {
          setImportWorkCategory(normalizeWorkCategory(wt));
        }
      })
      .catch(() => {
        /* keep default */
      });
    return () => {
      cancelled = true;
    };
  }, [importOpen, salesPointId]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedImportSearch(importSearch.trim()), 300);
    return () => clearTimeout(timer);
  }, [importSearch]);

  useEffect(() => {
    if (!importOpen || !salesPointId) return;

    let cancelled = false;
    const loadMaster = async () => {
      setLoadingMaster(true);
      try {
        const rows = await productSalesPointAPI.searchMaster(
          salesPointId,
          debouncedImportSearch,
          100,
          importWorkCategory !== "all" ? importWorkCategory : undefined
        );
        if (!cancelled) {
          setMasterProducts(rows);
          setSelectedMasterIds((prev) =>
            prev.filter((id) => rows.some((row) => row.id === id))
          );
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setAlert({
            type: "error",
            message: e instanceof Error ? e.message : "Failed to load master products",
          });
        }
      } finally {
        if (!cancelled) setLoadingMaster(false);
      }
    };

    void loadMaster();
    return () => {
      cancelled = true;
    };
  }, [importOpen, salesPointId, debouncedImportSearch, importWorkCategory]);

  const allMasterSelected =
    masterProducts.length > 0 &&
    masterProducts.every((p) => selectedMasterIds.includes(p.id));

  const someMasterSelected =
    masterProducts.some((p) => selectedMasterIds.includes(p.id)) && !allMasterSelected;

  const toggleMaster = (id: string) => {
    setSelectedMasterIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllMaster = () => {
    if (allMasterSelected) {
      setSelectedMasterIds([]);
    } else {
      setSelectedMasterIds(masterProducts.map((p) => p.id));
    }
  };

  const handleBulkImport = async () => {
    if (!salesPointId || selectedMasterIds.length === 0) return;
    setImporting(true);
    try {
      const result = await productSalesPointAPI.importProductsBulk(salesPointId, {
        productMasterIds: selectedMasterIds,
      });
      closeImportDialog();
      const skippedNote =
        result.skippedCount > 0 ? ` (${result.skippedCount} already assigned)` : "";
      setAlert({
        type: "success",
        message: `${result.importedCount} product(s) imported${skippedNote}`,
      });
      await queryClient.invalidateQueries({ queryKey: ["productSalesPointList"] });
    } catch (e: unknown) {
      setAlert({
        type: "error",
        message: e instanceof Error ? e.message : "Import failed",
      });
    } finally {
      setImporting(false);
    }
  };

  const handleImportAllByCategory = async () => {
    if (!salesPointId || importWorkCategory === "all") return;
    setImporting(true);
    try {
      const result = await productSalesPointAPI.importProductsBulk(salesPointId, {
        workCategory: importWorkCategory,
      });
      closeImportDialog();
      const skippedNote =
        result.skippedCount > 0 ? ` (${result.skippedCount} already assigned)` : "";
      setAlert({
        type: "success",
        message: `${result.importedCount} product(s) imported for ${workCategoryLabel(importWorkCategory)}${skippedNote}`,
      });
      await queryClient.invalidateQueries({ queryKey: ["productSalesPointList"] });
    } catch (e: unknown) {
      setAlert({
        type: "error",
        message: e instanceof Error ? e.message : "Import failed",
      });
    } finally {
      setImporting(false);
    }
  };

  const selectedCountLabel = useMemo(
    () => `${selectedMasterIds.length} selected`,
    [selectedMasterIds.length]
  );

  const openEdit = async (row: ProductSalesPointItem) => {
    setViewRow(null);
    setEditRow(row);
    setEditForm(rowToEditForm(row));
    setSchemeForm(emptySchemeForm());
    setLoadingSchemes(true);
    try {
      setSchemes(await productSalesPointAPI.listSchemes(row.id));
    } catch {
      setSchemes([]);
    } finally {
      setLoadingSchemes(false);
    }
  };

  const openView = (row: ProductSalesPointItem) => {
    setViewRow(row);
  };

  const closeView = () => setViewRow(null);

  const closeEdit = () => {
    setEditRow(null);
    setEditForm(emptyEditForm());
    setSchemes([]);
    setSchemeForm(emptySchemeForm());
  };

  const addScheme = async () => {
    if (!editRow) return;
    if (!schemeForm.name.trim() || !schemeForm.schemePrice) {
      setAlert({ type: "error", message: "Scheme name and price are required." });
      return;
    }
    setSavingScheme(true);
    try {
      await productSalesPointAPI.createScheme(editRow.id, {
        name: schemeForm.name.trim(),
        schemePrice: Number(schemeForm.schemePrice) || 0,
        startsAt: schemeForm.startsAt,
        endsAt: schemeForm.endsAt,
      });
      setSchemes(await productSalesPointAPI.listSchemes(editRow.id));
      setSchemeForm(emptySchemeForm());
      setAlert({ type: "success", message: "Price scheme added." });
      await queryClient.invalidateQueries({ queryKey: ["productSalesPointList"] });
    } catch (e: unknown) {
      setAlert({
        type: "error",
        message: e instanceof Error ? e.message : "Failed to add scheme",
      });
    } finally {
      setSavingScheme(false);
    }
  };

  const removeScheme = async (schemeId: string) => {
    if (!editRow || !window.confirm("Delete this price scheme?")) return;
    try {
      await productSalesPointAPI.deleteScheme(schemeId);
      setSchemes(await productSalesPointAPI.listSchemes(editRow.id));
      setAlert({ type: "success", message: "Price scheme deleted." });
      await queryClient.invalidateQueries({ queryKey: ["productSalesPointList"] });
    } catch (e: unknown) {
      setAlert({
        type: "error",
        message: e instanceof Error ? e.message : "Failed to delete scheme",
      });
    }
  };

  const savePricing = async () => {
    if (!editRow) return;
    const name = editForm.name.trim();
    if (!name) {
      setAlert({ type: "error", message: "Name is required" });
      return;
    }
    if (!editForm.sku.trim()) {
      setAlert({ type: "error", message: "SKU is required" });
      return;
    }
    setSavingEdit(true);
    try {
      const payload: ProductSalesPointUpdateData = {
        name,
        sku: editForm.sku.trim(),
        barcode: editForm.barcode.trim() || null,
        itemCode: editForm.itemCode.trim() || null,
        hsnCode: editForm.hsnCode.trim() || null,
        unit: editForm.unit || "PCS",
        categoryId: editForm.categoryId || null,
        status: editForm.status,
        availability: editForm.availability,
        hideFromBilling: editForm.hideFromBilling,
        allowFractionalQty: editForm.allowFractionalQty,
        basePrice: Number(editForm.basePrice) || 0,
        purchasePrice: Number(editForm.purchasePrice) || 0,
        sellingPrice: Number(editForm.sellingPrice) || 0,
        wholesalePrice: editForm.wholesalePrice
          ? Number(editForm.wholesalePrice)
          : null,
        mrp: editForm.mrp ? Number(editForm.mrp) : null,
        gstPercentage: Number(editForm.gstPercentage) || 0,
        isTaxInclusive: editForm.isTaxInclusive,
        pricingMode: editForm.pricingMode,
        discountType: editForm.discountType || null,
        discountValue: editForm.discountType
          ? Number(editForm.discountValue) || 0
          : null,
        pricePerUnit:
          editForm.pricingMode === "AMOUNT_BASED" && editForm.pricePerUnit
            ? Number(editForm.pricePerUnit)
            : null,
      };
      await productSalesPointAPI.update(editRow.id, payload);
      closeEdit();
      setAlert({ type: "success", message: "Product updated successfully" });
      await queryClient.invalidateQueries({ queryKey: ["productSalesPointList"] });
    } catch (e: unknown) {
      setAlert({
        type: "error",
        message: e instanceof Error ? e.message : "Update failed",
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const removeItem = async (row: ProductSalesPointItem) => {
    if (!window.confirm(`Remove "${row.name}" from this salesPoint?`)) return;
    try {
      await productSalesPointAPI.remove(row.id);
      setAlert({ type: "success", message: "Product removed from salesPoint" });
      await queryClient.invalidateQueries({ queryKey: ["productSalesPointList"] });
    } catch (e: unknown) {
      setAlert({
        type: "error",
        message: e instanceof Error ? e.message : "Remove failed",
      });
    }
  };

  const toggleHideFromBilling = async (
    row: ProductSalesPointItem,
    hideFromBilling: boolean
  ) => {
    try {
      await productSalesPointAPI.update(row.id, { hideFromBilling });
      setAlert({
        type: "success",
        message: hideFromBilling
          ? "Hidden from billing"
          : "Visible in billing again",
      });
      await queryClient.invalidateQueries({ queryKey: ["productSalesPointList"] });
    } catch (e: unknown) {
      setAlert({
        type: "error",
        message:
          e instanceof Error ? e.message : "Failed to update billing visibility",
      });
    }
  };

  if (!canChangeScope && !salesPointId) {
    return (
      <Box>
        <PageHeader
          title={pageTitle}
          subtitle={
            isOperator
              ? "Add products from the master catalog for your salesPoint"
              : "SalesPoint products for billing"
          }
        />
        <Alert severity="warning">Your account has no salesPoint assigned.</Alert>
      </Box>
    );
  }

  const canManageCatalog = canCreate("product_sales_points") && !!salesPointId;

  return (
    <Box>
      <PageHeader
        title={pageTitle}
        subtitle={
          isOperator
            ? "Add new items on the spot or import from master catalog"
            : "SalesPoint products — billing uses these prices"
        }
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: pageTitle },
        ]}
        action={
          canManageCatalog ? (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => setCreateOpen(true)}
              >
                Add new
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileUploadIcon />}
                onClick={() => setImportOpen(true)}
              >
                {importLabel}
              </Button>
            </Stack>
          ) : undefined
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : "Failed to load products"}
        </Alert>
      )}

      {canChangeScope && !salesPointId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a branch and sales point to view products.
        </Alert>
      )}

      <DataListCard
        searchPlaceholder="Search by name / SKU…"
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        filters={scopeFilters}
        footer={
          salesPointId ? (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ width: "100%" }}
          />
          ) : undefined
        }
      >
        {!salesPointId ? (
          <Box py={6} textAlign="center">
            <Typography color="text.secondary">
              Choose branch and sales point above to load the catalog.
            </Typography>
          </Box>
        ) : isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 && !debouncedSearch ? (
          <Box py={6} textAlign="center">
            <Typography color="text.secondary" mb={2}>
              {isOperator
                ? "No products yet. Add a new item or import from the master catalog."
                : "No products assigned to this sales point yet."}
            </Typography>
            {canManageCatalog && (
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button variant="contained" onClick={() => setCreateOpen(true)}>
                  Add new
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FileUploadIcon />}
                  onClick={() => setImportOpen(true)}
                >
                  {isOperator ? "From catalog" : "Import from master"}
                </Button>
              </Stack>
            )}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={72}>S.No.</TableCell>
                  <SortableTableCell
                    label="Name"
                    field="name"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={onSort}
                  />
                  <SortableTableCell
                    label="SKU"
                    field="sku"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={onSort}
                  />
                  <SortableTableCell
                    label="Category"
                    field="category"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={onSort}
                  />
                  <SortableTableCell
                    label="Price (₹)"
                    field="sellingPrice"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    align="right"
                  />
                  <TableCell align="center">Hide billing</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row, index) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { color: "primary.main", textDecoration: "underline" },
                        }}
                        onClick={() => openView(row)}
                      >
                        {row.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.sku}</TableCell>
                    <TableCell>{row.category?.name ?? "—"}</TableCell>
                    <TableCell align="right">
                      {Number(row.sellingPrice).toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        size="small"
                        checked={Boolean(row.hideFromBilling)}
                        disabled={!canUpdate("product_sales_points")}
                        onChange={(_, checked) =>
                          void toggleHideFromBilling(row, checked)
                        }
                        inputProps={{ "aria-label": "Hide from billing" }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <TableActionButton
                          title="View details"
                          onClick={() => openView(row)}
                        >
                          <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                        </TableActionButton>
                        {canUpdate("product_sales_points") && (
                          <TableActionButton
                            title="Edit product"
                            variant="primary"
                            onClick={() => openEdit(row)}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                        {canDelete("product_sales_points") && (
                          <TableActionButton
                            title="Remove from salesPoint"
                            variant="danger"
                            onClick={() => removeItem(row)}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No products found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataListCard>

      <Dialog open={createOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add new product</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              required
              fullWidth
              autoFocus
              value={createForm.name}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, name: e.target.value }))
              }
            />
            <TextField
              label="Selling price (₹)"
              required
              type="number"
              fullWidth
              value={createForm.sellingPrice}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, sellingPrice: e.target.value }))
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Unit</InputLabel>
              <Select
                label="Unit"
                value={createForm.unit}
                onChange={(e) => {
                  const unit = String(e.target.value);
                  const loose = ["KG", "GM", "LTR", "ML"].includes(unit);
                  setCreateForm((f) => ({
                    ...f,
                    unit,
                    allowFractionalQty: loose ? true : f.allowFractionalQty,
                  }));
                }}
              >
                {["PCS", "KG", "GM", "LTR", "ML", "BOX", "PKT"].map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Barcode (optional)"
              fullWidth
              value={createForm.barcode}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, barcode: e.target.value }))
              }
            />
            <FormControl fullWidth>
              <InputLabel>Pricing</InputLabel>
              <Select
                label="Pricing"
                value={createForm.pricingMode}
                onChange={(e) =>
                  setCreateForm((f) => ({
                    ...f,
                    pricingMode: e.target.value as
                      | "QUANTITY_BASED"
                      | "AMOUNT_BASED",
                    allowFractionalQty:
                      e.target.value === "AMOUNT_BASED"
                        ? true
                        : f.allowFractionalQty,
                  }))
                }
              >
                <MenuItem value="QUANTITY_BASED">Quantity / count</MenuItem>
                <MenuItem value="AMOUNT_BASED">By amount (₹ → qty)</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={createForm.allowFractionalQty}
                  onChange={(e) =>
                    setCreateForm((f) => ({
                      ...f,
                      allowFractionalQty: e.target.checked,
                    }))
                  }
                />
              }
              label="Allow fractional qty (loose / weight)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateDirect}
            disabled={creating}
          >
            {creating ? "Saving…" : "Add to sales point"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importOpen} onClose={closeImportDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {isOperator ? "Add products to your salesPoint" : "Import products from master"}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>Work category</InputLabel>
              <Select
                value={importWorkCategory}
                label="Work category"
                onChange={(e) => setImportWorkCategory(e.target.value)}
              >
                <MenuItem value="all">All categories</MenuItem>
                {WORK_CATEGORY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              placeholder="Search master by name / SKU / barcode"
              value={importSearch}
              onChange={(e) => setImportSearch(e.target.value)}
              InputProps={{
                endAdornment: loadingMaster ? (
                  <CircularProgress size={18} />
                ) : undefined,
              }}
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allMasterSelected}
                    indeterminate={someMasterSelected}
                    onChange={toggleSelectAllMaster}
                    disabled={!masterProducts.length || loadingMaster}
                  />
                }
                label="Select all"
              />
              <Typography variant="body2" color="text.secondary">
                {selectedCountLabel}
              </Typography>
            </Stack>
          </Box>

          {loadingMaster && masterProducts.length === 0 ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : masterProducts.length === 0 ? (
            <Box py={4} textAlign="center">
              <Typography color="text.secondary">
                {debouncedImportSearch
                  ? "No matching master products"
                  : "No master products available to import"}
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding sx={{ maxHeight: 420, overflow: "auto" }}>
              {masterProducts.map((product) => {
                const checked = selectedMasterIds.includes(product.id);
                return (
                  <ListItem key={product.id} disablePadding divider>
                    <ListItemButton onClick={() => toggleMaster(product.id)} dense>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Checkbox
                          edge="start"
                          checked={checked}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600}>
                            {product.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {product.sku || "No SKU"} · ₹
                            {Number(product.sellingPrice).toFixed(2)}
                            {product.category?.name ? ` · ${product.category.name}` : ""}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={closeImportDialog} disabled={importing}>
            Cancel
          </Button>
          {importWorkCategory !== "all" && (
            <Button
              variant="outlined"
              onClick={handleImportAllByCategory}
              disabled={importing}
            >
              {importing
                ? "Importing…"
                : `Import all ${workCategoryLabel(importWorkCategory)}`}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleBulkImport}
            disabled={importing || selectedMasterIds.length === 0}
            startIcon={importing ? undefined : <FileUploadIcon />}
          >
            {importing ? (
              <CircularProgress size={22} color="inherit" />
            ) : isOperator ? (
              `Add selected (${selectedMasterIds.length})`
            ) : (
              `Import selected (${selectedMasterIds.length})`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editRow} onClose={closeEdit} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit product — {editRow?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update catalog details and sales-point prices here — no need to open Product Master.
          </Typography>

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Product details
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                required
                label="Name"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, name: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="SKU"
                value={editForm.sku}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, sku: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Barcode"
                value={editForm.barcode}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, barcode: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Item code"
                value={editForm.itemCode}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, itemCode: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="HSN"
                value={editForm.hsnCode}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, hsnCode: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  label="Unit"
                  value={editForm.unit}
                  onChange={(e) => {
                    const unit = String(e.target.value);
                    const loose = ["KG", "GM", "LTR", "ML"].includes(unit);
                    setEditForm((f) => ({
                      ...f,
                      unit,
                      allowFractionalQty: loose ? true : f.allowFractionalQty,
                    }));
                  }}
                >
                  {EDIT_UNITS.map((u) => (
                    <MenuItem key={u} value={u}>
                      {u}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={editForm.categoryId}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      categoryId: String(e.target.value),
                    }))
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={editForm.status}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      status: e.target.value as EditProductForm["status"],
                    }))
                  }
                >
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Availability</InputLabel>
                <Select
                  label="Availability"
                  value={editForm.availability}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      availability: e.target.value as EditProductForm["availability"],
                    }))
                  }
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="DINE_IN">Dine-in</MenuItem>
                  <MenuItem value="TAKEAWAY">Takeaway</MenuItem>
                  <MenuItem value="DELIVERY">Delivery</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={editForm.allowFractionalQty}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        allowFractionalQty: e.target.checked,
                      }))
                    }
                  />
                }
                label="Allow fractional qty"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={editForm.hideFromBilling}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        hideFromBilling: e.target.checked,
                      }))
                    }
                  />
                }
                label="Hide from billing"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Prices
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Base price (₹)"
                value={editForm.basePrice}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, basePrice: e.target.value }))
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Purchase price (₹)"
                value={editForm.purchasePrice}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, purchasePrice: e.target.value }))
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Selling price (₹)"
                value={editForm.sellingPrice}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, sellingPrice: e.target.value }))
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Wholesale price (₹)"
                value={editForm.wholesalePrice}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, wholesalePrice: e.target.value }))
                }
                helperText="Optional — used when POS is in wholesale mode"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="MRP (₹)"
                value={editForm.mrp}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, mrp: e.target.value }))
                }
                helperText="Optional — strike price on retail bills"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Discount
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Discount type</InputLabel>
                <Select
                  label="Discount type"
                  value={editForm.discountType}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      discountType: e.target.value as EditProductForm["discountType"],
                      discountValue: e.target.value ? f.discountValue : "",
                    }))
                  }
                >
                  <MenuItem value="">No discount</MenuItem>
                  <MenuItem value="PERCENT">Percentage (%)</MenuItem>
                  <MenuItem value="FLAT">Flat amount (₹)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {editForm.discountType && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  size="small"
                  label={
                    editForm.discountType === "PERCENT"
                      ? "Discount (%)"
                      : "Discount (₹)"
                  }
                  value={editForm.discountValue}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, discountValue: e.target.value }))
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {editForm.discountType === "PERCENT" ? "%" : "₹"}
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ min: 0, step: "0.01" }}
                />
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Tax (GST)
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                size="small"
                label="GST (%)"
                value={editForm.gstPercentage}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, gstPercentage: e.target.value }))
                }
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{ min: 0, max: 100, step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isTaxInclusive}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, isTaxInclusive: e.target.checked }))
                    }
                  />
                }
                label="Price includes tax (tax inclusive)"
              />
              <FormHelperText sx={{ ml: 0 }}>
                If on, selling price already includes GST.
              </FormHelperText>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Date-effective schemes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Active schemes override retail price on POS for the date range.
          </Typography>
          {loadingSchemes ? (
            <CircularProgress size={22} />
          ) : (
            <>
              {schemes.length > 0 && (
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {schemes.map((s) => (
                    <Stack
                      key={s.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {s.name} · ₹{Number(s.schemePrice).toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.startsAt} → {s.endsAt}
                          {editRow?.activeScheme?.id === s.id ? " · active today" : ""}
                        </Typography>
                      </Box>
                      {canUpdate("product_sales_points") && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => void removeScheme(s.id)}
                        >
                          Delete
                        </Button>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}
              {canUpdate("product_sales_points") && (
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Scheme name"
                      value={schemeForm.name}
                      onChange={(e) =>
                        setSchemeForm((f) => ({ ...f, name: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Scheme price"
                      value={schemeForm.schemePrice}
                      onChange={(e) =>
                        setSchemeForm((f) => ({
                          ...f,
                          schemePrice: e.target.value,
                        }))
                      }
                      inputProps={{ min: 0, step: "0.01" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Starts"
                      InputLabelProps={{ shrink: true }}
                      value={schemeForm.startsAt}
                      onChange={(e) =>
                        setSchemeForm((f) => ({ ...f, startsAt: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Ends"
                      InputLabelProps={{ shrink: true }}
                      value={schemeForm.endsAt}
                      onChange={(e) =>
                        setSchemeForm((f) => ({ ...f, endsAt: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="outlined"
                      onClick={() => void addScheme()}
                      disabled={savingScheme}
                    >
                      {savingScheme ? "Adding…" : "Add scheme"}
                    </Button>
                  </Grid>
                </Grid>
              )}
            </>
          )}

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Billing mode
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Pricing mode</InputLabel>
                <Select
                  label="Pricing mode"
                  value={editForm.pricingMode}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      pricingMode: e.target.value as EditProductForm["pricingMode"],
                      allowFractionalQty:
                        e.target.value === "AMOUNT_BASED"
                          ? true
                          : f.allowFractionalQty,
                    }))
                  }
                >
                  <MenuItem value="QUANTITY_BASED">Quantity based</MenuItem>
                  <MenuItem value="AMOUNT_BASED">Amount based</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {editForm.pricingMode === "AMOUNT_BASED" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  size="small"
                  label="Price per unit (₹)"
                  value={editForm.pricePerUnit}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, pricePerUnit: e.target.value }))
                  }
                  helperText="Used when customer enters an amount"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: "0.0001" }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeEdit} disabled={savingEdit}>
            Cancel
          </Button>
          <Button variant="contained" onClick={savePricing} disabled={savingEdit}>
            {savingEdit ? <CircularProgress size={22} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!viewRow} onClose={closeView} maxWidth="md" fullWidth>
        <DialogTitle>
          Product details — {viewRow?.name}
        </DialogTitle>
        <DialogContent>
          {viewRow && (
            <Box sx={{ mt: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Chip
                  size="small"
                  label={viewRow.status === "INACTIVE" ? "Inactive" : "Active"}
                  color={viewRow.status === "INACTIVE" ? "default" : "success"}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    viewRow.pricingMode === "AMOUNT_BASED"
                      ? "Amount based"
                      : "Quantity based"
                  }
                />
                {viewRow.allowFractionalQty ? (
                  <Chip size="small" variant="outlined" label="Fractional qty" />
                ) : null}
              </Stack>

              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Product
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <DetailField label="Name" value={viewRow.name} />
                <DetailField label="SKU" value={viewRow.sku} />
                <DetailField label="Barcode" value={viewRow.barcode} />
                <DetailField label="Item code" value={viewRow.itemCode} />
                <DetailField label="HSN" value={viewRow.hsnCode} />
                <DetailField label="Unit" value={viewRow.unit} />
                <DetailField
                  label="Category"
                  value={viewRow.category?.name}
                />
                <DetailField
                  label="Product type"
                  value={viewRow.productType}
                />
                <DetailField
                  label="Availability"
                  value={viewRow.availability || "ALL"}
                />
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Pricing
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <DetailField label="Base price" value={money(viewRow.basePrice)} />
                <DetailField
                  label="Purchase price"
                  value={money(viewRow.purchasePrice)}
                />
                <DetailField
                  label="Selling price"
                  value={money(viewRow.sellingPrice)}
                />
                <DetailField
                  label="Wholesale price"
                  value={
                    viewRow.wholesalePrice != null
                      ? money(viewRow.wholesalePrice)
                      : "—"
                  }
                />
                <DetailField
                  label="MRP"
                  value={viewRow.mrp != null ? money(viewRow.mrp) : "—"}
                />
                <DetailField
                  label="Effective selling"
                  value={
                    viewRow.effectiveSellingPrice != null
                      ? money(viewRow.effectiveSellingPrice)
                      : money(viewRow.sellingPrice)
                  }
                />
                <DetailField
                  label="Discount"
                  value={
                    viewRow.discountType
                      ? `${viewRow.discountType} · ${viewRow.discountValue ?? 0}${
                          viewRow.discountType === "PERCENT" ? "%" : ""
                        }`
                      : "None"
                  }
                />
                <DetailField
                  label="Price / unit"
                  value={
                    viewRow.pricePerUnit != null
                      ? money(viewRow.pricePerUnit)
                      : "—"
                  }
                />
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Tax
              </Typography>
              <Grid container spacing={2}>
                <DetailField
                  label="GST"
                  value={`${Number(viewRow.gstPercentage || 0)}%`}
                />
                <DetailField
                  label="Tax inclusive"
                  value={viewRow.isTaxInclusive ? "Yes" : "No"}
                />
                {viewRow.activeScheme ? (
                  <DetailField
                    label="Active scheme"
                    value={`${viewRow.activeScheme.name} · ${money(
                      viewRow.activeScheme.schemePrice
                    )} (${viewRow.activeScheme.startsAt} → ${viewRow.activeScheme.endsAt})`}
                  />
                ) : (
                  <DetailField label="Active scheme" value="None" />
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeView}>Close</Button>
          {viewRow && canUpdate("product_sales_points") && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => {
                if (viewRow) void openEdit(viewRow);
              }}
            >
              Edit
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductListPage;
